import React, { type JSX } from "react";
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { selectIsFullScreen, setFullScreen } from "../../DocumentPreviewSlice_Store";
import PdfFactViewer, { type CitationSearchProgress } from "../pdf/PdfFactViewer";
import ExpandedDocumentView from "../../ExpandedDocumentView_Component";
import { ProgressBar } from "./PreviewStates";
import FactsPanel from "./FactsPanel";
import { type DocumentData, type PreviewData, filenameOf } from "./documentPreviewTypes";
import { type DocumentFacts } from "./useDocumentFacts";
import { type DocumentActions } from "./useDocumentActions";

/** PDF.js is given the SAS URL where possible so it can fetch pages on demand. */
function pdfSource(previewData: PreviewData | null): string | Blob | undefined {
  const sasData = previewData?.sasData;
  return sasData?.sasUrl && !sasData.isSharePoint ? sasData.sasUrl : previewData?.contentBlob;
}

function PreviewToolbar({
  objectUrl,
  actions,
  onExpand,
}: {
  objectUrl: string | null;
  actions: DocumentActions;
  onExpand: () => void;
}): JSX.Element {
  return (
    <div className="absolute top-2 right-5 z-20 flex flex-col gap-1">
      <button
        onClick={onExpand}
        className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors"
        title="Expand to full screen"
        aria-label="Expand to full screen"
      >
        <ArrowsPointingOutIcon className="h-5 w-5 text-cvc-blue" />
      </button>
      {objectUrl && (
        <button
          onClick={actions.download}
          className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors"
          title="Download"
          aria-label="Download document"
        >
          <ArrowDownTrayIcon className="h-5 w-5 text-cvc-blue" />
        </button>
      )}
      {actions.canOpen && (
        <button
          onClick={() => void actions.openExternally()}
          disabled={actions.opening}
          className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors disabled:opacity-50"
          title="Open document"
          aria-label="Open document"
        >
          <ArrowTopRightOnSquareIcon className="h-5 w-5 text-cvc-blue" />
        </button>
      )}
    </div>
  );
}

/** Covers the viewer while the citation is being located in the document. */
function FindingCitationOverlay({
  progress,
}: {
  progress: CitationSearchProgress | null;
}): JSX.Element {
  const hasProgress = Boolean(progress && progress.percentage > 0);

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-cvc-canvas">
      <div className="text-center">
        <div className="mb-4">
          <DocumentIcon className="h-12 w-12 mx-auto text-cvc-blue mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Finding citation...</p>
        </div>
        <ProgressBar
          percentage={hasProgress ? progress.percentage : null}
          caption={
            progress && progress.totalPages > 1
              ? `Searching page ${progress.currentPage} of ${progress.totalPages}...`
              : "Locating text in document..."
          }
        />
      </div>
    </div>
  );
}

/** Tabular facts are highlighted by the source table, so a plain viewer will do. */
function TabularPdfView({
  objectUrl,
  filename,
  onDownload,
}: {
  objectUrl: string;
  filename: string;
  onDownload: () => void;
}): JSX.Element {
  return (
    <object
      data={objectUrl}
      type="application/pdf"
      className="w-full h-full"
      aria-label={`PDF preview of ${filename}`}
    >
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-sm text-gray-600 mb-4">Your browser doesn't support PDF preview.</p>
          <button
            onClick={onDownload}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </object>
  );
}

export interface PdfPreviewPaneProps {
  document: DocumentData;
  previewData: PreviewData | null;
  objectUrl: string;
  facts: DocumentFacts;
  actions: DocumentActions;
}

/** The PDF viewer, its floating toolbar and the facts cited from the document. */
export default function PdfPreviewPane({
  document,
  previewData,
  objectUrl,
  facts,
  actions,
}: PdfPreviewPaneProps): JSX.Element {
  const dispatch = useAppDispatch();
  const isFullScreen = useAppSelector(selectIsFullScreen);
  const filename = filenameOf(document);

  if (facts.isTabular) {
    return (
      <TabularPdfView objectUrl={objectUrl} filename={filename} onDownload={actions.download} />
    );
  }

  const viewerProps = {
    blob: pdfSource(previewData),
    factHighlights: facts.factHighlights,
    activeHighlightId: facts.activeHighlightId,
    searchTrigger: facts.searchTrigger,
    onCitationSearchStart: facts.onCitationSearchStart,
    onCitationSearchProgress: facts.onCitationSearchProgress,
    onCitationSearchComplete: facts.onCitationSearchComplete,
  };

  return (
    <>
      <div className="flex-1 overflow-hidden min-h-0 relative">
        {!isFullScreen && (
          <PreviewToolbar
            objectUrl={objectUrl}
            actions={actions}
            onExpand={() => dispatch(setFullScreen(true))}
          />
        )}
        {facts.isFindingCitation && (
          <FindingCitationOverlay progress={facts.citationSearchProgress} />
        )}
        {!isFullScreen && <PdfFactViewer {...viewerProps} />}
        {isFullScreen && (
          <ExpandedDocumentView
            documentName={document.originalFilename || document.documentName || "Document"}
            filePath={document.filePath}
            sharepointSiteId={document.sharepointSiteId}
            sharepointDriveId={document.sharepointDriveId}
            sharepointItemId={document.sharepointItemId}
            objectUrl={objectUrl}
            onClose={() => dispatch(setFullScreen(false))}
            onDownload={actions.download}
            {...viewerProps}
          />
        )}
      </div>
      {facts.sortedDocumentFacts.length > 0 && <FactsPanel facts={facts} />}
    </>
  );
}
